import { IContentModel } from '@ninjacart/nc-nk-core';
import { addEventParam, INinjaEvent, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import {
  FallBackImage,
  isInvalidPostOwnerId,
  KissanEventType,
  truncateName,
  useAppPersistenceStore,
  useGlobalLoginContext,
  useProfileStore,
} from '@ninjacart/nc-nk-store';
import {
  Avatar,
  Button,
  Paragraph,
  Text,
  useTimeAgo,
  View,
  XStack,
  YStack,
} from '@ninjacart/nc-nk-ui';
import { handleContactPermission, useRouter } from '@ninjacart/nc-nk-web-deps';
import { ChevronDown, MoreVertical, Plus } from '@tamagui/lucide-icons';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';

export const ListHeader = ({
  content,
  onReportBottomSheetOpen,
  index,
  pageName,
  updateConnectionStatus,
}: {
  content: IContentModel;
  onReportBottomSheetOpen: Function;
  index: number;
  pageName?: string;
  updateConnectionStatus?: Function;
}) => {
  const { push } = useRouter();
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);
  const { t } = useTranslation();
  const appLanguage = useAppPersistenceStore((state) => state.appLanguage);
  const [showFollowBtn, setShowFollowBtn] = useState(
    hasUserLoggedIn() ? !content?.following : true
  );
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const fields = useGlobalLoginContext(['openLoginSheet','previousPage', 'navigationParams']);
  const setNavigationParams = fields.navigationParams.set;
  const isFollowStatusUpdatingRef = useRef(false);
  const [isFollowing, setIsFollowing] = useState(hasUserLoggedIn() ? content?.following : false);
  // const createdAt = getRelativeTime(content?.created_at);

  // useEffect(() => {
  //   if (isVideoFeedActive && isActiveTourGuides?.enableFollowTour && canStart && !hasActiveTourGuides?.homeHeader)  {
  //     start(homeHeaderTourSteps.step1?.id);
  //     const homepageProfileClickEvent :INinjaEvent = {
  //       name: KissanEventType.TOURGUIDE_LAUNCH_SUCCESSFUL_FEEDUSERFOLLOW,
  //       screenDetails: { screen: 'feed page' }
  //     };
  //     enqueue(homepageProfileClickEvent);
  //   }
  // }, [isVideoFeedActive, isActiveTourGuides?.enableFollowTour, canStart,hasActiveTourGuides?.homeHeader]);

  // useEffect(() => {
  //   eventEmitter?.on('stop', handleOnStop);
  //   return () => {
  //     eventEmitter?.on('stop', handleOnStop);
  //   };
  // }, [eventEmitter]);

  // const handleOnStop = async () => {
  //   setHasActiveTourGuides('homeHeader', true);
  // };

  // const tourContent = (item) => {
  //   return (
  //     <YStack width={220}>
  //       <Text
  //         fontFamily={'Gilroy'}
  //         color={'#000'}
  //         fontSize={20}
  //         fontWeight={'700'}
  //         marginBottom={2}
  //         textAlign={'center'}
  //       >
  //         {item.heading}
  //       </Text>
  //       <Text fontFamily={'Gilroy'} color={'#000'} fontSize={16}>
  //         {item.text}
  //       </Text>
  //     </YStack>
  //   );
  // };

  const onNavigateProfileTab = () => {
    if (!hasUserLoggedIn()) {
      fields['openLoginSheet'].set(true);
      fields['previousPage'].set('FEED PAGE');
      setNavigationParams({
        name: '',
        routeName: isInvalidPostOwnerId(content.postOwnerId)
          ? { name: `(tabs)` }
          : {
              name: `profile/[id]`,
              params: { id: content?.postOwnerId, name: truncateName(content?.name, 15) },
            },
        description: `${t('page.viewOnProfilePage')}`,
        screenName: 'FEED PAGE',
      });
      return;
    }

    handleEventOnProfileClick(content.postOwnerId);

    if (isInvalidPostOwnerId(content.postOwnerId)) {
      return;
    }

    push({
      pathname: `/profile/${content.postOwnerId}`,
      query: {
        name: truncateName(content?.name, 15),
      },
    });
    // push(`/profile/${content.postOwnerId}?name=${truncateName(content?.name, 15)}`);
  };

  const handleEventOnProfileClick = (ownerId) => {
    const homepageProfileClickEvent: INinjaEvent = {
      name: KissanEventType.PROFILE_CLICK_HOMEPAGE,
      screenDetails: { screen: 'profile page' },
      eventParams: [addEventParam('otherUserId', ownerId)],
    };
    enqueue(homepageProfileClickEvent);
  };

  const handleFollowButtonClick = async () => {
    if (!hasUserLoggedIn()) {
      fields['openLoginSheet'].set(true);
      fields['previousPage'].set('FEED PAGE');
      setNavigationParams({
        name: '',
        routeName: isInvalidPostOwnerId(content.postOwnerId)
          ? { name: `(tabs)` }
          : {
              name: `profile/[id]`,
              params: { id: content?.postOwnerId, name: truncateName(content?.name, 15) },
            },
        description: `${t('page.toFollow')}`,
        screenName: 'FEED PAGE',
      });
      return;
    }
    setIsFollowing(!isFollowing);
    if (isFollowStatusUpdatingRef.current) return;

    isFollowStatusUpdatingRef.current = true;

    try {
      const eventType = isFollowing
        ? KissanEventType.FEEDPAGE_USER_UNFOLLOW
        : KissanEventType.FEEDPAGE_USER_FOLLOW;
      handleFollowEventClick(content, eventType);

      if (updateConnectionStatus) {
        const response = await updateConnectionStatus(isFollowing, content.postOwnerId);
        handleContactPermission();
      }
    } catch (error) {
      console.error('Error handling follow button click', error);
    } finally {
      isFollowStatusUpdatingRef.current = false;
    }
  };

  const handleFollowEventClick = (content, event) => {
    const homepageProfileClickEvent: INinjaEvent = {
      name: event,
      screenDetails: { screen: 'follow list' },
      eventParams: [
        addEventParam('followedUserId', content.postOwnerId),
        addEventParam('videoId', content.id),
      ],
    };
    enqueue(homepageProfileClickEvent);
  };

  return (
    <>
      <XStack
        testID="kisan_tId_NavigateProfileTab"
        padding={8}
        zIndex={1}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        onPress={onNavigateProfileTab}
        maxHeight={60}
        minHeight={60}
      >
        <XStack>
          <Avatar circular size="$4">
            <Avatar.Image
              accessibilityLabel="Nate Wienert"
              src={content?.avatar ?? FallBackImage}
            />
            <Avatar.Fallback delayMs={600} />
          </Avatar>
          <YStack paddingLeft={8} display="flex" justifyContent="center">
            <Paragraph
              color={'$color'}
              fontSize={appLanguage == 'ta' ? 12 : 16}
              fontWeight={'600'}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {truncateName(content?.name, 18)}
            </Paragraph>

            <Text
              paddingTop={2}
              fontSize={appLanguage == 'ta' ? 10 : 12}
              height={20}
              fontWeight={'500'}
              color={'$darkGrey'}
            >
              {content?.created_at && useTimeAgo(content?.created_at)}
            </Text>
          </YStack>
        </XStack>

        <XStack>
          {showFollowBtn && !pageName ? (
            <XStack marginRight={10}>
              <Button
                testID="kisan_tId_FollowButtonClick"
                size={30}
                width={isFollowing ? 120 : 100}
                backgroundColor={'$cardbackground'}
                borderColor={'$primary'}
                borderWidth={1}
                borderRadius={6}
                justifyContent="center"
                onPress={(event) => {
                  if (isFollowStatusUpdatingRef.current) return;
                  event.stopPropagation();
                  handleFollowButtonClick();
                }}
                disabled={isFollowStatusUpdatingRef.current}
              >
                <View
                  flex={1}
                  flexDirection="row"
                  padding={4}
                  justifyContent="center"
                  alignItems="center"
                >
                  {!isFollowing && <Plus size={18} color="$primary" />}
                  <Text
                    fontSize={appLanguage == 'ta' ? 12 : 14}
                    fontWeight="600"
                    color={'$primary'}
                  >
                    {isFollowing ? t('page.following') : t('page.follow')}
                  </Text>
                  {isFollowing && (
                    <ChevronDown style={{ marginTop: 2 }} size={18} color="$primary" />
                  )}
                </View>
              </Button>
            </XStack>
          ) : null}
          <XStack paddingTop={2}>
            <Pressable
              testID="kisan_tId_ReportBottomSheetOpen"
              onPress={() => {
                onReportBottomSheetOpen(content, index);
              }}
            >
              <MoreVertical color={'$color'} />
            </Pressable>
          </XStack>
        </XStack>
      </XStack>
    </>
  );
};
